import type {ReactNode} from 'react';
import {useState} from 'react';

import styled from '@emotion/styled';
import {media, spacing} from '@nfq/react-grid';

import {Badge} from 'UI/components/layout/Badge';
import {ImageSlider} from 'UI/components/modules/ImageSlider';
import type {SliderImage} from 'UI/components/modules/ImageSlider/ImageSlider';
import {ImageZoomModal} from 'UI/components/modules/ImageZoomModal';
import {ResponsiveImage} from 'UI/components/modules/ResponsiveImage';
import type {ImageSource} from 'UI/components/modules/ResponsiveImage/ResponsiveImage';

import {FullscreenIcon} from 'Images/icons';

/** A single category badge shown in the hero, with its optional predefined icon. */
export interface HeroCategoryBadge {
    /** The category's predefined icon, if any. */
    icon?: ReactNode;
    /** The category's display name. */
    label: string;
}

/**
 * Custom hook backing the `HeroSection` component.
 * Manages the zoom-modal open state and the active slider index, and derives the
 * effective single-image / slider-mode display flags from the provided images.
 *
 * @param images   Optional array of images for the slider. A slider is used when there is more than one.
 * @param imageUrl Optional single background image URL, used as a fallback when `images` has 0 or 1 entries.
 * @returns An object containing the derived display state, and the setters for the zoom/slide state.
 */
const useHeroImageState = (images: SliderImage[] | undefined, imageUrl: string | undefined) => {
    const [isZoomOpen, setIsZoomOpen] = useState(false);
    const [activeSlide, setActiveSlide] = useState(0);

    const hasSlider = Boolean(images && images.length > 1);
    const singleImage = !hasSlider && images?.length === 1 ? images[0] : null;
    const singleUrl = singleImage?.url ?? imageUrl;
    const hasImage = hasSlider || Boolean(singleUrl);

    return {
        activeSlide,
        hasImage,
        hasSlider,
        isZoomOpen,
        setActiveSlide,
        setIsZoomOpen,
        singleImage,
        singleUrl
    };
};

/**
 * The props for the `HeroSection` component.
 * Configures a full-width hero image with gradient overlay, badges, title, and subtitle.
 */
interface ComponentProps {
    /** Optional category badges (e.g. "Historical Mystery"), each with its predefined icon. */
    categories?: HeroCategoryBadge[];
    /** Optional text shown below the title (e.g. Subtitle or tagline). */
    description?: string;
    /** Optional duration label displayed as badge (e.g. "1h 15m"). */
    durationLabel?: string;
    /** The URL of the hero background image. */
    imageAlt?: string;
    /** Focal point X coordinate (0–100) for single image mode. */
    imageFocalX?: number | null;
    /** Focal point Y coordinate (0–100) for single image mode. */
    imageFocalY?: number | null;
    /** Array of images for the slider. When provided with more than one entry, a slider is shown. */
    images?: SliderImage[];
    /** Responsive srcSet for single image mode. */
    imageSrcSet?: ImageSource[];
    /** The URL of the hero background image. */
    imageUrl?: string;
    /** Optional original price shown struck through before `priceLabel` (e.g. For promo discounts). */
    originalPriceLabel?: string;
    /** Optional price label displayed as badge (e.g. "€4.99" or "Kostenlos"). */
    priceLabel?: string;
    /**
     * The `testId` property represents a unique identifier assigned to a component for testing purposes.
     * It is applied as `data-cy` attribute to enable reliable targeting in Cypress tests.
     *
     * @default 'HeroSection'
     */
    testId?: string;
    /** The large display title of the walk. */
    title: string;
}

/**
 * A full-width hero section with background image, gradient overlay, and floating badges.
 * Matches the Stitch "tour_details" design: dramatic hero with category, duration, and price pills.
 * When multiple images are provided, renders an interactive slider with dot indicators.
 *
 * @param props                    The component props.
 * @param props.categories         Optional category badges, each with an optional icon.
 * @param props.description        Optional subtitle/tagline text.
 * @param props.durationLabel      Optional duration badge label.
 * @param props.imageAlt           Alt text for the hero image.
 * @param props.imageFocalX        Focal point X coordinate (0–100) for single image mode.
 * @param props.imageFocalY        Focal point Y coordinate (0–100) for single image mode.
 * @param props.imageSrcSet        Responsive srcSet for single image mode.
 * @param props.imageUrl           URL for the hero background image.
 * @param props.images             Optional array of slider images. Takes priority over imageUrl when length > 1.
 * @param props.originalPriceLabel Optional struck-through original price for promo discounts.
 * @param props.priceLabel         Optional price badge label.
 * @param props.testId             An optional test ID used for automation selectors. Defaults to `'HeroSection'`.
 * @param props.title              The main display title.
 * @returns A JSX element rendering the HeroSection.
 *
 * @example
 * ```tsx
 * <HeroSection
 *     categories={[{icon: <CastleIcon />, label: 'Historical Mystery'}]}
 *     durationLabel="1h 15m"
 *     imageUrl="/media/hero.jpg"
 *     priceLabel="€4.99"
 *     title="Medieval Augsburg Mystery"
 * />
 * ```
 */
const HeroSection = ({
    categories,
    description,
    durationLabel,
    imageAlt = '',
    imageFocalX,
    imageFocalY,
    images,
    imageSrcSet,
    imageUrl,
    originalPriceLabel,
    priceLabel,
    testId = 'HeroSection',
    title
}: ComponentProps) => {
    const {
        activeSlide, hasImage, hasSlider, isZoomOpen, setActiveSlide, setIsZoomOpen, singleImage, singleUrl
    } = useHeroImageState(images, imageUrl);

    return (
        <Wrapper data-cy={testId}>
            <MediaBox>
                {hasSlider && images
                    ? (
                        <SliderWrapper>
                            <ImageSlider
                                dotPositionBottom="25px"
                                images={images}
                                priority
                                onSlideChange={setActiveSlide}
                            />
                        </SliderWrapper>
                    )
                    : singleUrl && (
                        <ResponsiveImage
                            alt={singleImage?.alt ?? imageAlt}
                            focalX={singleImage?.focalX ?? imageFocalX}
                            focalY={singleImage?.focalY ?? imageFocalY}
                            sizes="100vw"
                            src={singleUrl}
                            srcSet={singleImage?.srcSet ?? imageSrcSet}
                            testId={`${testId}-image`}
                            priority
                        />
                    )}
                <GradientOverlay />
                {hasImage && (
                    <ZoomButton
                        aria-label="Bild vergrößern"
                        data-cy={`${testId}-zoom`}
                        type="button"
                        onClick={() => setIsZoomOpen(true)}
                    >
                        <FullscreenIcon height={20} width={20} />
                    </ZoomButton>
                )}
            </MediaBox>
            <Content>
                <BadgeRow data-cy={`${testId}-badges`}>
                    {categories?.map(category => (
                        <Badge key={category.label} icon={category.icon} label={category.label} variant="category" />
                    ))}
                    {durationLabel && <Badge label={durationLabel} variant="metric" />}
                    {priceLabel && (
                        <PriceBadge data-cy={`${testId}-price`}>
                            {originalPriceLabel && <StrikePrice>{originalPriceLabel}</StrikePrice>}
                            {priceLabel}
                        </PriceBadge>
                    )}
                </BadgeRow>
                <Title data-cy={`${testId}-title`}>{title}</Title>
                {description && <Subtitle>{description}</Subtitle>}
            </Content>
            <ImageZoomModal
                imageAlt={singleImage?.alt ?? imageAlt}
                imageFocalX={singleImage?.focalX ?? imageFocalX}
                imageFocalY={singleImage?.focalY ?? imageFocalY}
                images={images}
                imageSrcSet={singleImage?.srcSet ?? imageSrcSet}
                imageUrl={singleUrl}
                initialSlide={activeSlide}
                isOpen={isZoomOpen}
                onClose={() => setIsZoomOpen(false)}
            />
        </Wrapper>
    );
};

HeroSection.displayName = 'HeroSection';

export {HeroSection};

// ── Styled ──────────────────────────────────────────────────

const Wrapper = styled.section`
    position: relative;
    width: 100%;
`;

const MediaBox = styled.div`
    height: 53rem;
    overflow: hidden;
    position: relative;
    width: 100%;
`;

const SliderWrapper = styled.div`
    height: 100%;
    width: 100%;
`;

const GradientOverlay = styled.div`
    backdrop-filter: blur(10px);
    background: #3a3a3a;
    inset: 0;
    mask: linear-gradient(to top, #000 0%, transparent 50%);
    mix-blend-mode: multiply;
    pointer-events: none;
    position: absolute;

    ${media('lg')} {
        display: none;
    }
`;

const ZoomButton = styled.button`
    align-items: center;
    backdrop-filter: blur(8px);
    background: rgba(0, 0, 0, 0.4);
    border: none;
    border-radius: 50%;
    color: ${({theme}) => theme.colors.white};
    cursor: pointer;
    display: flex;
    height: 3.6rem;
    justify-content: center;
    position: absolute;
    right: ${spacing(4)};
    top: ${spacing(4)};
    width: 3.6rem;
    z-index: 5;

    &:active {
        background: rgba(0, 0, 0, 0.6);
    }
`;

const Content = styled.div`
    bottom: 0;
    display: flex;
    flex-direction: column;
    gap: ${spacing(2)};
    left: 0;
    padding: ${spacing(8)} ${spacing(8)} ${spacing(10)};
    pointer-events: none;
    position: absolute;
    width: 100%;

    ${media('lg')} {
        padding: ${spacing(8)} ${spacing(8)} 0;
        pointer-events: auto;
        position: static;
    }
`;

const BadgeRow = styled.div`
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: ${spacing(2)};
    margin-bottom: ${spacing(2)};
`;

const PriceBadge = styled.span`
    background: ${({theme}) => theme.colors.primaryDefault};
    border-radius: 9999px;
    color: ${({theme}) => theme.colors.white};
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.2rem;
    font-weight: 700;
    padding: ${spacing(1.5)} ${spacing(4)};
`;

const StrikePrice = styled.s`
    font-weight: 400;
    margin-right: ${spacing(1.5)};
    opacity: 0.8;
`;

const Title = styled.h1`
    color: ${({theme}) => theme.colors.white};
    font-family: ${({theme}) => theme.fonts.NotoSerif};
    font-size: 4.4rem;
    font-weight: 700;
    hyphens: auto;
    line-height: 1.1;
    margin: 0;
    max-width: 800px;
    width: 100%;

    ${media('lg')} {
        color: ${({theme}) => theme.colors.primaryDefault};
    }
`;

const Subtitle = styled.p`
    color: rgba(255, 255, 255, 0.8);
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.6rem;
    font-style: italic;
    margin: 0;

    ${media('lg')} {
        color: ${({theme}) => theme.colors.inkSecondary};
    }
`;